import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import client from '../api/client';
import { C, Toast, useNotify } from '../utils/styles';

export default function Register() {

  const [params]       = useSearchParams();
  const planId         = params.get('plan');

  const [plan,     setPlan]     = useState(null);
  const [loading,  setLoading]  = useState(!!planId);
  const [saving,   setSaving]   = useState(false);
  const [notification, notify]  = useNotify();

  const [form, setForm] = useState({
    first_name:   '',
    last_name:    '',
    email:        '',
    password:     '',
    confirm:      '',
    company_name: '',
  });
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (!planId) { setLoading(false); return; }
    client.get(`/api/billing/plans/public/${planId}`)
      .then(({ data }) => setPlan(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [planId]);

  const field = (key, label, type='text', placeholder='') => (
    <div>
      <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>{label}</label>
      <input
        type={type} placeholder={placeholder}
        value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
        style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:14,outline:'none',boxSizing:'border-box'}}
      />
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name||!form.email||!form.password) return notify('Please fill all required fields','error');
    if (form.password !== form.confirm) return notify('Passwords do not match','error');
    if (form.password.length < 8) return notify('Password must be at least 8 characters','error');
    setSaving(true);
    try {
      const { data } = await client.post('/api/auth/register', {
        first_name:   form.first_name.trim(),
        last_name:    form.last_name.trim(),
        email:        form.email.trim().toLowerCase(),
        password:     form.password,
        company_name: form.company_name.trim(),
        plan_id:      planId || undefined,
      });
      // Store token — must use window.location.href (not navigate)
      // so AuthContext fully re-initializes with the new token
      localStorage.setItem('dx_token', data.token);
      client.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      // Force full page reload so AuthContext picks up the new token
      window.location.href = `/onboarding${planId?`?plan=${planId}`:''}`;
    } catch (err) {
      notify(err.response?.data?.error || 'Registration failed', 'error');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f9fafb'}}>
        <div style={{width:40,height:40,border:`3px solid ${C.primary}`,borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
      </div>
    );
  }

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#f0f9ff 0%,#f9fafb 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <Toast {...notification}/>
      <div style={{width:'100%',maxWidth:460}}>
        {/* Logo / Brand */}
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{fontSize:26,fontWeight:800,color:C.primary,marginBottom:4}}>Digital Xtension</div>
          <div style={{fontSize:14,color:'#6b7280'}}>Create your account and start your free trial</div>
        </div>

        {/* Plan badge */}
        {plan && (
          <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:'12px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:36,height:36,background:'#22c55e18',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <i className="ti ti-crown" style={{fontSize:18,color:'#16a34a'}}/>
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:'#166534'}}>{plan.name} Plan</div>
              <div style={{fontSize:12,color:'#16a34a'}}>
                {plan.trial_days > 0 ? `${plan.trial_days}-day free trial` : 'No trial'}
                {plan.price_monthly_inr > 0 ? `, then ₹${plan.price_monthly_inr.toLocaleString()}/month` : ' · Free'}
              </div>
            </div>
            <div style={{marginLeft:'auto',textAlign:'right'}}>
              <div style={{fontSize:20,fontWeight:800,color:'#166534'}}>
                {plan.trial_days > 0 ? 'Free' : plan.price_monthly_inr > 0 ? `₹${plan.price_monthly_inr}` : 'Free'}
              </div>
              {plan.trial_days > 0 && <div style={{fontSize:10,color:'#16a34a'}}>for {plan.trial_days} days</div>}
            </div>
          </div>
        )}

        {/* Form card */}
        <div style={{background:'#fff',borderRadius:16,boxShadow:'0 4px 24px rgba(0,0,0,0.08)',padding:32}}>
          <div style={{fontWeight:700,fontSize:18,color:'#111827',marginBottom:20}}>Create your account</div>
          <form onSubmit={handleSubmit} style={{display:'grid',gap:14}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {field('first_name','First Name *','text','John')}
              {field('last_name','Last Name','text','Smith')}
            </div>
            {field('company_name','Company / Agency Name','text','Acme Marketing')}
            {field('email','Email Address *','email','you@company.com')}
            <div>
              <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Password * <span style={{color:'#9ca3af',fontWeight:400}}>(min 8 characters)</span></label>
              <div style={{position:'relative'}}>
                <input type={showPass?'text':'password'} placeholder="••••••••" value={form.password}
                  onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                  style={{width:'100%',padding:'10px 38px 10px 12px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:14,outline:'none',boxSizing:'border-box'}}/>
                <button type="button" onClick={()=>setShowPass(!showPass)}
                  style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af'}}>
                  <i className={`ti ${showPass?'ti-eye-off':'ti-eye'}`} style={{fontSize:17}}/>
                </button>
              </div>
            </div>
            <div>
              <label style={{fontSize:12,fontWeight:500,color:'#374151',marginBottom:4,display:'block'}}>Confirm Password *</label>
              <input type="password" placeholder="••••••••" value={form.confirm}
                onChange={e=>setForm(f=>({...f,confirm:e.target.value}))}
                style={{width:'100%',padding:'10px 12px',borderRadius:8,border:`1px solid ${form.confirm&&form.confirm!==form.password?'#ef4444':'#e5e7eb'}`,fontSize:14,outline:'none',boxSizing:'border-box'}}/>
              {form.confirm && form.confirm !== form.password && (
                <div style={{fontSize:11,color:'#ef4444',marginTop:3}}>Passwords do not match</div>
              )}
            </div>
            <div style={{fontSize:12,color:'#6b7280',lineHeight:1.5}}>
              By creating an account, you agree to our{' '}
              <span style={{color:C.primary,cursor:'pointer',textDecoration:'underline'}}>Terms of Service</span> and{' '}
              <span style={{color:C.primary,cursor:'pointer',textDecoration:'underline'}}>Privacy Policy</span>.
            </div>
            <button type="submit" disabled={saving}
              style={{padding:'12px',borderRadius:8,border:'none',background:C.primary,color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:saving?0.7:1}}>
              <i className={`ti ${saving?'ti-loader ti-spin':'ti-arrow-right'}`}/>
              {saving ? 'Creating account...' : plan?.trial_days > 0 ? `Start ${plan.trial_days}-Day Free Trial` : 'Create Account'}
            </button>
          </form>
        </div>

        <div style={{textAlign:'center',marginTop:16,fontSize:13,color:'#6b7280'}}>
          Already have an account?{' '}
          <Link to="/login" style={{color:C.primary,fontWeight:500}}>Sign in</Link>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
